import { Item } from "@aitisal/contracts/restaurant/menu";
import { useCart } from "../../stores/useCart";
import { Button } from "@aitisal/ui/retail-storefront/button";
import { Card, CardContent } from "@aitisal/ui/retail-storefront/card";
import { useNavigate } from "@tanstack/react-router";
import { Heart } from "lucide-react";
import React, { useState } from "react";
import ResponsiveImage from "../ResponsiveImage";
import { getTitle } from "../../helper/helper";
import { useWishlistStore } from "../../stores/wishlistStore";

interface ProductCardProps {
  item: Item;
  isNew?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ item }) => {
  const navigate = useNavigate();
  const [isAdding, setIsAdding] = useState(false);

  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, wishlist } = useWishlistStore();

  const isWishlisted = wishlist.some((i) => i.itemId === item.itemId);

  const hasDiscount = !!(
    item.compareAtPrice &&
    item.compareAtPrice > item.price
  );

  const discountPercent =
    hasDiscount && item.compareAtPrice
      ? Math.round(
          ((item.compareAtPrice - item.price) / item.compareAtPrice) * 100,
        )
      : 0;

  const handleCardClick = () => {
    navigate({ to: "/c/product/$itemId", params: { itemId: item.itemId } });
  };

  const hasVariants = item.attributes && item.attributes.length > 0;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAdding(true);
    if (
      (item.customizations && item.customizations.length > 0) ||
      hasVariants
    ) {
      navigate({ to: "/c/product/$itemId", params: { itemId: item.itemId } });
    } else {
      addToCart(item, [], 1);
      setTimeout(() => setIsAdding(false), 800);
    }
  };

  const toggleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isWishlisted) {
      removeFromWishlist(item.itemId);
    } else {
      addToWishlist(item);
    }
  };

  return (
    <Card
      className="group relative w-full max-w-full flex flex-col overflow-visible rounded-xl border border-gray-100 bg-white cursor-pointer transition-all duration-300"
      onClick={handleCardClick}
    >
      {/* Image Section */}
      <div className="relative aspect-[5/5] w-full overflow-hidden bg-[#F3F3F3] rounded-t-xl">
        <div className="absolute top-3 right-3 flex flex-col gap-2 md:hidden z-10">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleWishlist}
            className="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm shadow-sm"
          >
            <Heart
              className={`h-4 w-4 ${isWishlisted ? "fill-red-500 text-red-500" : "text-gray-600"}`}
            />
          </Button>
        </div>

        {hasDiscount && (
          <span
            className="absolute top-3 left-3 z-10 rounded-full px-2 py-0.5 text-[11px] font-bold text-white shadow-sm"
            style={{
              backgroundColor:
                "var(--discounted-price-text-color, #f44336)",
            }}
          >
            -{discountPercent}%
          </span>
        )}

        <ResponsiveImage
          imageObject={item.images?.[0]}
          sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
          alt={getTitle(item.name)}
          className="absolute inset-0 h-full w-full transition-transform duration-500 group-hover:scale-105"
          style={{ height: "100%" }}
        />
      </div>

      {/* Content Section */}
      <CardContent className="p-0 relative h-[148px] md:h-[80px]">
        <div className="absolute left-0 right-0 top-0 z-30 bg-white transition-all duration-300 ease-in-out md:group-hover:-translate-y-[20px] md:group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.12)] rounded-b-xl border-t border-transparent">
          <div className="flex flex-col px-4 h-[80px] justify-center bg-white rounded-t-xl">
            <h3 className="text-sm md:text-base font-medium text-gray-800 line-clamp-1">
              {getTitle(item.name)}
            </h3>
            <div className="flex flex-col mt-0.5 leading-tight">
              <span
                className="text-base md:text-xl font-bold whitespace-nowrap"
                style={
                  hasDiscount
                    ? { color: "var(--discounted-price-text-color, #f44336)" }
                    : { color: "#000" }
                }
              >
                Rs. {(item.price / 100).toFixed(2)}
              </span>
              {hasDiscount && (
                <span className="text-[11px] md:text-xs text-gray-400 line-through whitespace-nowrap">
                  Rs. {((item.compareAtPrice ?? 0) / 100).toFixed(2)}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 px-4 h-[65px] pb-4 opacity-100 bg-white rounded-b-xl md:h-0 md:pb-0 md:opacity-0 md:overflow-hidden transition-all duration-300 md:group-hover:h-[65px] md:group-hover:opacity-100 md:group-hover:pb-4">
            <Button
              variant="outline"
              onClick={handleAddToCart}
              disabled={isAdding}
              className={`flex-1 rounded-full border-[var(--action-buttons-color,#110042)] text-[var(--action-buttons-text-color,#110042)] hover:bg-[var(--action-buttons-color,#110042)] hover:text-[var(--action-buttons-text-color,#ffffff)] transition-all h-10 font-semibold ${isAdding ? "bg-green-600 border-green-600 text-white" : ""}`}
            >
              {isAdding ? "Added!" : "Add To Cart"}
            </Button>
            <Button
              onClick={toggleWishlist}
              variant="ghost"
              size="icon"
              className="rounded-full hidden md:inline-flex"
            >
              <Heart
                className={`h-5 w-5 ${isWishlisted ? "fill-red-500 text-red-500" : "text-gray-400"}`}
              />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductCard;